import React from "react";
import { useTranslation } from "react-i18next";
import {
  EmailBuilderButtonSizeControl,
  EmailBuilderColorOverrideField,
} from "../shell/panel/blockSettings/EmailBuilderBlockSettingsShared";
import {
  isButtonBorderInherited,
  isButtonFillInherited,
  isButtonTextInherited,
  resolveButtonBorderColor,
  resolveButtonFillColor,
  resolveButtonTextColor,
  type ButtonTheme,
} from "../domain/button-theme";
import { selectEmailSettings } from "../domain/selectors";
import { useEditorStore } from "../domain/redux/hooks";
import { bandBorderHasVisibleWidth } from "../operations/emailBuilderBandBorder";
import { buttonSizePatch } from "../domain/blocks/button-presentation";
import type { BorderStyle, ButtonChrome, ButtonSize } from "../domain/types";
import { EmailBuilderSettingsAccordion } from "./accordion/EmailBuilderSettingsAccordion";
import { EmailBuilderSettingsBorderFields } from "./controls/EmailBuilderSettingsBorderFields";
import { APPEARANCE_NESTED_DEFAULT_EXPANDED } from "./settingsPanelDefaults";
import {
  buttonBorderAddDefault,
  isButtonBorderInactive,
} from "./settingsSectionActivation";

const BUTTON_BORDER_STYLES = ["none", "solid"] as const;

type ButtonAppearanceValue = ButtonChrome & { size: ButtonSize };

type ButtonAppearanceSectionProps = {
  value: ButtonAppearanceValue;
  sizeDefault: ButtonSize;
  patch: (next: Partial<ButtonAppearanceValue>) => void;
};

function buttonChromeToBorderStyle(
  chrome: Pick<ButtonChrome, "borderWidth" | "borderColor">,
  theme: ButtonTheme,
): BorderStyle {
  const width = Math.max(0, chrome.borderWidth);
  return {
    width: { top: width, right: width, bottom: width, left: width },
    style: width > 0 ? "solid" : "none",
    color: resolveButtonBorderColor(chrome, theme),
  };
}

function buttonChromeFromBorderStyle(
  border: BorderStyle,
  theme: ButtonTheme,
): Pick<ButtonChrome, "borderWidth" | "borderColor"> {
  if (!bandBorderHasVisibleWidth(border.width) || border.style === "none") {
    return { borderWidth: 0 };
  }

  return {
    borderWidth: border.width.top,
    borderColor: border.color === theme.buttonBorderColor ? null : border.color,
  };
}

export function ButtonAppearanceSection({
  value,
  sizeDefault,
  patch,
}: ButtonAppearanceSectionProps) {
  const { t } = useTranslation();
  const settings = useEditorStore(selectEmailSettings);
  const buttonFillColor = resolveButtonFillColor(value, settings);
  const borderActive = !isButtonBorderInactive(value.borderWidth);

  return (
    <>
      <EmailBuilderButtonSizeControl
        value={value.size}
        defaultValue={sizeDefault}
        onChange={size => {
          patch(buttonSizePatch(size));
        }}
      />
      <EmailBuilderSettingsAccordion
        defaultExpandedSectionIds={APPEARANCE_NESTED_DEFAULT_EXPANDED}
        nested
      >
        <EmailBuilderSettingsAccordion.Section
          sectionId="appearance-background"
          title={t("Background")}
          variant="nested"
        >
          <EmailBuilderColorOverrideField
            showFieldLabel={false}
            label={t("Fill")}
            color={resolveButtonFillColor(value, settings)}
            hasOverride={!isButtonFillInherited(value)}
            onChange={color => {
              patch({
                backgroundColor:
                  color === settings.buttonFillColor ? null : color,
              });
            }}
            onReset={() => patch({ backgroundColor: null })}
          />
          <EmailBuilderColorOverrideField
            showFieldLabel={false}
            label={t("Text")}
            color={resolveButtonTextColor(value, settings)}
            hasOverride={!isButtonTextInherited(value)}
            contrastAgainst={buttonFillColor}
            contrastPreset="button-fill"
            onChange={color => {
              patch({
                color: color === settings.buttonTextColor ? null : color,
              });
            }}
            onReset={() => patch({ color: null })}
          />
        </EmailBuilderSettingsAccordion.Section>
        <EmailBuilderSettingsAccordion.Section
          sectionId="appearance-border"
          title={t("Border")}
          variant="nested"
          activation={{
            mode: "optional",
            isActive: borderActive,
            onAdd: () => patch(buttonBorderAddDefault()),
          }}
        >
          <EmailBuilderSettingsBorderFields
            border={buttonChromeToBorderStyle(value, settings)}
            hideCustomizeSides
            styles={BUTTON_BORDER_STYLES}
            colorOverride={{
              hasOverride: !isButtonBorderInherited(value),
              onReset: () => patch({ borderColor: null }),
            }}
            onChange={border => {
              patch(buttonChromeFromBorderStyle(border, settings));
            }}
          />
        </EmailBuilderSettingsAccordion.Section>
      </EmailBuilderSettingsAccordion>
    </>
  );
}
